// src/context/QuotaContext.js
// ✅ Updated: NO session-based plan anymore.
// - Removes remaining_sessions + all "session plan" logic/messages
// - Keeps FREE plan question quota (e.g., 30 questions) + MONTHLY expiry
// - upgradeMessage is ALWAYS a plain string (safe to render anywhere)

import React, { createContext, useContext, useState } from "react";
import { isIOSApp } from "../utils/platform"; // ✅ Make sure this path is correct

// 🌐 Create context
export const QuotaContext = createContext();

// 🔁 Provider
export const QuotaProvider = ({ children }) => {
  const [quota, setQuota] = useState({
    plan_type: "free",
    remaining_questions: 0,
    expires_at: null,
    chatInputDisabled: false,
  });

  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshQuota = async (fromSessionData = null) => {
    try {
      if (!fromSessionData) {
        console.warn("⚠️ No quota/billing data provided to refreshQuota(). Skipping.");
        return;
      }

      const quotaData = {
        plan_type: fromSessionData.plan_type || "free", // "free" | "monthly"
        remaining_questions: Number(fromSessionData.remaining_questions || 0),
        expires_at: fromSessionData.expires_at || null,
      };

      const now = new Date();
      const expires = quotaData.expires_at ? new Date(quotaData.expires_at) : null;

      // ✅ Disable input when:
      // - free plan has no remaining questions
      // - monthly plan expired
      const chatInputDisabled =
        (quotaData.plan_type === "free" && quotaData.remaining_questions <= 0) ||
        (quotaData.plan_type === "monthly" && expires && now >= expires);

      // ✅ Upgrade messaging (strings only)
      let message = "";
      let shouldShow = false;
      const isIOS = isIOSApp();

      if (quotaData.plan_type === "monthly" && expires && now >= expires) {
        message = isIOS
          ? "Your monthly plan has expired. Please upgrade at www.asklawly.com"
          : "Your monthly plan has expired. Please upgrade in Billing.";
        shouldShow = true;
      } else if (quotaData.plan_type === "free" && quotaData.remaining_questions <= 0) {
        message = isIOS
          ? "You’ve used all your free questions. Please upgrade at www.asklawly.com"
          : "You’ve used all your free questions. Please upgrade in Billing.";
        shouldShow = true;
      }

      console.log("🧾 upgradeMessage:", message);
      console.log("🔔 showUpgrade:", shouldShow);

      setQuota({
        ...quotaData,
        chatInputDisabled,
      });

      setUpgradeMessage(message);
      setShowUpgrade(shouldShow);
    } catch (err) {
      console.error("❌ Error in refreshQuota:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <QuotaContext.Provider
      value={{
        ...quota,
        refreshQuota,
        loading,
        upgradeMessage,
        showUpgrade,
      }}
    >
      {children}
    </QuotaContext.Provider>
  );
};

// ✅ Hook for child components
export const useQuota = () => useContext(QuotaContext);
